/**
 * FRM Wishlist — lightweight localStorage-backed wishlist shared by every
 * product card (homepage carousels, collection/search grid, PDP
 * recommendations) and the PDP's own wishlist button.
 *
 * Any button marked [data-wish-toggle] with data-wish-handle/title/price/
 * image/url attributes is auto-wired on load and after any DOM mutation
 * (collection AJAX filtering replaces #ProductGridContainer's innerHTML,
 * so a MutationObserver — not just one DOMContentLoaded pass — is needed
 * to keep newly-injected cards in sync).
 */
(function () {
  var STORAGE_KEY = 'frm_wishlist';

  function getAll() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveAll(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* localStorage unavailable (private mode, quota, etc.) — fail silently */
    }
  }

  function isSaved(handle) {
    return getAll().some(function (i) { return i.handle === handle; });
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function escapeAttr(str) {
    return String(str == null ? '' : str).replace(/"/g, '&quot;');
  }

  function syncButtons(handle, saved) {
    var selector = '[data-wish-toggle][data-wish-handle="' + handle.replace(/"/g, '\\"') + '"]';
    document.querySelectorAll(selector).forEach(function (btn) {
      btn.classList.toggle('is-wished', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      btn.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  function updateBadge() {
    var badge = document.getElementById('frm-wishlist-count');
    if (badge) badge.textContent = getAll().length;
  }

  function renderDrawer() {
    var list = document.getElementById('frm-wishlist-items');
    if (!list) return;
    var items = getAll();
    if (items.length === 0) {
      list.innerHTML = '<p class="frm-wishlist-empty">Your wishlist is empty.</p>';
      return;
    }
    list.innerHTML = items.map(function (item) {
      return (
        '<div class="frm-wishlist-item">' +
          '<a href="' + escapeAttr(item.url) + '" class="frm-wishlist-item__img">' +
            (item.image ? '<img src="' + escapeAttr(item.image) + '" alt="' + escapeAttr(item.title) + '" loading="lazy">' : '') +
          '</a>' +
          '<div class="frm-wishlist-item__info">' +
            '<a href="' + escapeAttr(item.url) + '" class="frm-wishlist-item__title">' + escapeHtml(item.title) + '</a>' +
            '<span class="frm-wishlist-item__price">' + escapeHtml(item.price) + '</span>' +
          '</div>' +
          '<button type="button" class="frm-wishlist-item__remove" data-wish-remove="' + escapeAttr(item.handle) + '" aria-label="Remove ' + escapeAttr(item.title) + ' from wishlist">&times;</button>' +
        '</div>'
      );
    }).join('');
  }

  function toggle(data) {
    var items = getAll();
    var idx = -1;
    for (var i = 0; i < items.length; i++) {
      if (items[i].handle === data.handle) { idx = i; break; }
    }
    var nowSaved;
    if (idx > -1) {
      items.splice(idx, 1);
      nowSaved = false;
    } else {
      items.push(data);
      nowSaved = true;
    }
    saveAll(items);
    syncButtons(data.handle, nowSaved);
    renderDrawer();
    updateBadge();
    return nowSaved;
  }

  function remove(handle) {
    saveAll(getAll().filter(function (i) { return i.handle !== handle; }));
    syncButtons(handle, false);
    renderDrawer();
    updateBadge();
  }

  function initButtons(root) {
    var scope = root || document;
    var items = getAll();
    var saved = {};
    items.forEach(function (i) { saved[i.handle] = true; });

    scope.querySelectorAll('[data-wish-toggle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-wish-handle');
      if (!handle) return;

      if (saved[handle]) {
        btn.classList.add('is-wished');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        btn.setAttribute('aria-pressed', 'false');
      }

      if (btn.getAttribute('data-wish-bound') === '1') return;
      btn.setAttribute('data-wish-bound', '1');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggle({
          handle: handle,
          title: btn.getAttribute('data-wish-title') || '',
          price: btn.getAttribute('data-wish-price') || '',
          image: btn.getAttribute('data-wish-image') || '',
          url: btn.getAttribute('data-wish-url') || '#'
        });
      });
    });
  }

  function openDrawer() {
    var drawer = document.getElementById('frm-wishlist-drawer');
    var overlay = document.getElementById('frm-wishlist-overlay');
    if (!drawer) return;
    drawer.removeAttribute('inert');
    drawer.classList.add('active');
    if (overlay) overlay.classList.add('active');
    document.body.classList.add('frm-wishlist-open');
  }

  function closeDrawer() {
    var drawer = document.getElementById('frm-wishlist-drawer');
    var overlay = document.getElementById('frm-wishlist-overlay');
    if (!drawer) return;
    drawer.classList.remove('active');
    if (overlay) overlay.classList.remove('active');
    document.body.classList.remove('frm-wishlist-open');
    drawer.setAttribute('inert', '');
  }

  document.addEventListener('DOMContentLoaded', function () {
    initButtons();
    renderDrawer();
    updateBadge();

    var toggleBtn = document.getElementById('frm-wishlist-toggle');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openDrawer();
      });
    }

    var closeBtn = document.getElementById('frm-wishlist-close');
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    var overlay = document.getElementById('frm-wishlist-overlay');
    if (overlay) overlay.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });

    document.addEventListener('click', function (e) {
      var removeBtn = e.target.closest && e.target.closest('[data-wish-remove]');
      if (removeBtn) remove(removeBtn.getAttribute('data-wish-remove'));
    });

    // Collection/search AJAX filtering (facets.js) replaces
    // #ProductGridContainer's innerHTML wholesale — watch for that (and
    // any other dynamically-injected cards, e.g. quick view) and re-wire.
    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        initButtons();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });

  window.FRMWishlist = { toggle: toggle, remove: remove, isSaved: isSaved, getAll: getAll, initButtons: initButtons };
})();
